/**
 * Interpréteur de contraintes déclaratives pour le générateur paramétrique
 * d'exercices (§8 de la spec) — mini-langage fermé, exécuté déterministement
 * (pas d'IA). Les formes `compare`/`integer` évaluent leurs expressions via
 * `evalExpr` (@jeraisonne/db/shared/expr) : celui-ci ne sait faire que de
 * l'arithmétique, donc les comparaisons (`>`, `!=`…) sont modélisées comme un
 * opérateur séparé plutôt que comme une extension de la grammaire d'expr.ts.
 */

import { evalExpr, toEvalScope, type Scope } from '../shared/expr';
import type { BlueprintConstraint } from './schemas';

function evaluateConstraint(constraint: BlueprintConstraint, scope: Scope): boolean {
  switch (constraint.type) {
    case 'compare': {
      // evalExpr normalise ses identifiants en minuscules — toEvalScope aligne
      // le scope (noms Blueprint camelCase autorisés, cf. expr.ts).
      const evalScope = toEvalScope(scope);
      const left = evalExpr(constraint.left, evalScope);
      const right = evalExpr(constraint.right, evalScope);
      switch (constraint.operator) {
        case '>':
          return left > right;
        case '>=':
          return left >= right;
        case '<':
          return left < right;
        case '<=':
          return left <= right;
        case '==':
          return left === right;
        case '!=':
          return left !== right;
      }
      break;
    }
    case 'range': {
      const value = scope[constraint.variable];
      if (value === undefined) {
        throw new Error(`Contrainte "range" sur une variable inconnue : ${constraint.variable}`);
      }
      return value >= constraint.min && value <= constraint.max;
    }
    case 'integer': {
      return Number.isInteger(evalExpr(constraint.expression, toEvalScope(scope)));
    }
  }
}

/** Vrai si TOUTES les contraintes sont satisfaites dans la portée donnée. */
export function validateConstraints(
  constraints: readonly BlueprintConstraint[],
  scope: Scope,
): boolean {
  return constraints.every((c) => evaluateConstraint(c, scope));
}
